import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import {
  fetchMonDossierSponsor,
  fetchSponsorPlans,
  soumettreSouscription,
  uploadFichierSponsor,
} from '../services/api';
import { nomFamille } from '../utils/nom';
import FileUpload from '../components/FileUpload';
import VisuelDetenu from '../components/VisuelsDetenus';
import ChampSiret from '../components/ChampSiret';
import InscriptionSponsor from '../components/InscriptionSponsor';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

const schema = yup.object({
  nom: yup.string().required('Le nom est requis'),
  email: yup.string().email('Email invalide').required('L\'email est requis'),
  contactNom: yup.string().required('Le nom du contact est requis'),
  telephone: yup.string().nullable(),
  adresse: yup.string().nullable(),
  // Facultatif : vide passe, un SIREN (9 chiffres) comme un SIRET (14) aussi.
  siret: yup
    .string()
    .nullable()
    .test(
      'siret',
      'Saisissez 14 chiffres (SIRET) ou 9 (SIREN), ou laissez vide.',
      (valeur) => {
        const chiffres = (valeur ?? '').replace(/\D/g, '');
        return chiffres.length === 0 || chiffres.length === 9 || chiffres.length === 14;
      }
    ),
  siteWeb: yup.string().url('URL invalide').nullable(),
  message: yup.string().nullable(),
  planLivret: yup.string().nullable(),
  planEcocup: yup.string().nullable(),
  planTshirt: yup.string().nullable(),
  consentement: yup.boolean().oneOf([true], 'Vous devez accepter le traitement des données'),
});

type FormData = yup.InferType<typeof schema>;

/**
 * « Devenir sponsor » pour le public, annuaire des sponsors pour le bureau.
 *
 * Un membre du bureau connecté n'a pas à remplir un formulaire de
 * parrainage : ce qu'il vient chercher sous cette entrée, c'est la liste de
 * ceux qui ont déjà souscrit. Les montants et le suivi financier restent
 * réservés au bureau — un compte sponsor ne voit pas les dossiers des autres.
 */
export default function SponsorPage() {
  const { user, isLoading: authLoading } = useAuth();

  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true ||
    user?.roles?.includes('ROLE_ADMIN') === true;

  // Un visiteur deja connecte a forcement un compte : lui en faire creer un
  // second n'aurait pas de sens, et rattacherait sa demande au mauvais.
  const [compteCree, setCompteCree] = useState(false);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [visuelFile, setVisuelFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Un sponsor qui renouvelle ne ressaisit rien : ses coordonnées sont
  // rappelées, repliées, et ne s'ouvrent que s'il veut les corriger.
  const [coordonneesOuvertes, setCoordonneesOuvertes] = useState(false);
  const [reprendreVisuels, setReprendreVisuels] = useState(true);

  const { data: plans } = useQuery({
    queryKey: ['sponsor-plans'],
    queryFn: fetchSponsorPlans,
  });

  const { data: dossier } = useQuery({
    queryKey: ['mon-dossier-sponsor'],
    queryFn: fetchMonDossierSponsor,
    enabled: user != null && !estBureau,
    retry: false,
  });

  const connu = dossier?.connu === true;
  const visuelsDetenus = dossier?.visuels ?? {};
  const aDesVisuels = Boolean(visuelsDetenus.logoHd || visuelsDetenus.visuel);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      planLivret: '',
      planEcocup: '',
      planTshirt: '',
    },
  });

  const mutation = useMutation({
    mutationFn: soumettreSouscription,
  });

  useEffect(() => {
    if (user?.email && !getValues('email')) {
      setValue('email', user.email);
    }
  }, [user, getValues, setValue]);

  // Le formulaire part de ce que le comité sait déjà : le sponsor n'a plus
  // qu'à choisir son plan. Rien n'écrase une saisie en cours.
  useEffect(() => {
    const c = dossier?.coordonnees;
    if (!c) return;

    for (const [champ, valeur] of Object.entries({
      nom: c.nom,
      siret: c.siret,
      adresse: c.adresse,
      contactNom: c.contactNom,
      telephone: c.telephone,
      email: c.email,
      siteWeb: c.siteWeb,
    }) as [keyof FormData, string | null][]) {
      if (valeur && !getValues(champ)) {
        setValue(champ, valeur);
      }
    }
  }, [dossier, getValues, setValue]);

  const selectedLivret = watch('planLivret');
  const selectedEcocup = watch('planEcocup');
  const selectedTshirt = watch('planTshirt');

  const montant =
    (plans?.livret?.find((p: any) => p.key === selectedLivret)?.montant || 0) +
    (plans?.ecocup?.find((p: any) => p.key === selectedEcocup)?.montant || 0) +
    (plans?.tshirt?.find((p: any) => p.key === selectedTshirt)?.montant || 0);

  const onSubmit = async (data: FormData) => {
    setUploadStatus(null);
    try {
      const result = await mutation.mutateAsync({
        ...data,
        // Les visuels déjà détenus sont repris côté serveur : le sponsor n'a
        // pas à renvoyer un logo que le comité a en main.
        reprendreVisuels: connu && aDesVisuels && reprendreVisuels,
      });
      const souscriptionId = result.souscriptionId;

      if (souscriptionId) {
        const uploads = [];
        if (logoFile) uploads.push(uploadFichierSponsor(souscriptionId, logoFile, 'logo_hd'));
        if (visuelFile) uploads.push(uploadFichierSponsor(souscriptionId, visuelFile, 'visuel'));

        if (uploads.length > 0) {
          await Promise.all(uploads);
          setUploadStatus({ success: true, message: 'Vos fichiers ont aussi ete envoyes.' });
        }
      }

      reset();
      setLogoFile(null);
      setVisuelFile(null);
    } catch {
      // L'erreur est deja visible via mutation.isError
    }
  };

  const entete = (
    <div>
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Devenir sponsor</h1>
      <p className="mt-2 lg:mt-3 text-sm lg:text-base text-gray-600 max-w-3xl dark:text-gray-400">
        Soutenez la fête locale et bénéficiez d'une visibilité sur livret, écocups, t-shirts et nos réseaux.
      </p>
    </div>
  );

  if (authLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Le bureau connecté voit l'annuaire, pas le formulaire : le parcours de
  // souscription ne le concerne pas.
  if (estBureau) {
    // Le répertoire vit désormais dans la page Sponsors unifiée, avec les
    // devis, factures et avoirs : l'ancienne adresse y mène.
    return <Navigate to="/bureau/facturation?onglet=repertoire" replace />;
  }

  // Etape 1 : le compte. Le formulaire de parrainage ne s'ouvre qu'ensuite,
  // pour que la demande soit rattachee a un titulaire identifiable.
  if (!user && !compteCree) {
    return (
      <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
        {entete}
        <ol className="flex items-center gap-3 text-sm">
          <li className="font-semibold text-primary-700">1. Votre compte</li>
          <li className="text-gray-400">→</li>
          <li className="text-gray-400">2. Votre demande</li>
        </ol>
        <InscriptionSponsor onCree={() => setCompteCree(true)} />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      {entete}

      <ol className="flex items-center gap-3 text-sm">
        <li className="text-green-700 inline-flex items-center gap-1">
          <Check className="w-4 h-4" /> Votre compte
        </li>
        <li className="text-gray-400">→</li>
        <li className="font-semibold text-primary-700">2. Votre demande</li>
      </ol>

      {mutation.isSuccess ? (
        <div className="card bg-green-50 border-green-200 text-green-800 lg:p-6">
          <div className="flex items-center gap-2 font-semibold">
            <Check className="w-5 h-5" />
            Demande envoyée !
          </div>
          <p className="mt-2 text-sm lg:text-base">{mutation.data?.message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 lg:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Identité */}
            <section className="card space-y-3 lg:p-6">
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold lg:text-lg">Vos coordonnées</h2>
                {/* Le comité connaît déjà ce sponsor : lui refaire remplir sa
                    fiche à chaque renouvellement n'a pas de sens. Elle est
                    rappelée, et ne s'ouvre que s'il veut la corriger. */}
                {connu && !coordonneesOuvertes && (
                  <button
                    type="button"
                    onClick={() => setCoordonneesOuvertes(true)}
                    className="text-sm text-primary-700 dark:text-primary-400 hover:underline shrink-0"
                  >
                    Modifier
                  </button>
                )}
              </div>

              {connu && !coordonneesOuvertes ? (
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4 text-sm space-y-1">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {watch('nom') || dossier?.coordonnees?.nom}
                  </p>
                  {(watch('siret') || dossier?.coordonnees?.siret) && (
                    <p className="text-gray-600 dark:text-gray-400 font-mono text-xs">
                      SIRET {watch('siret') || dossier?.coordonnees?.siret}
                    </p>
                  )}
                  <p className="text-gray-600 dark:text-gray-400">
                    {watch('contactNom') || dossier?.coordonnees?.contactNom}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    {watch('email') || dossier?.coordonnees?.email}
                  </p>
                  {(watch('telephone') || dossier?.coordonnees?.telephone) && (
                    <p className="text-gray-600 dark:text-gray-400">
                      {watch('telephone') || dossier?.coordonnees?.telephone}
                    </p>
                  )}
                  {(watch('adresse') || dossier?.coordonnees?.adresse) && (
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {watch('adresse') || dossier?.coordonnees?.adresse}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                    Ces informations seront reprises sur votre devis. Corrigez-les si elles ont
                    changé.
                  </p>
                </div>
              ) : (
                <>
              {/* Le SIRET vient en premier : il pré-remplit le nom et l'adresse.
                  Placé plus bas, il arrivait après une saisie manuelle qu'il
                  s'interdit d'écraser — le service rendu était perdu. */}
              <ChampSiret
                value={watch('siret') ?? ''}
                onChange={(v) => setValue('siret', v, { shouldValidate: true })}
                onEntreprise={(entreprise) => {
                  // Ne rien ecraser de ce qui a deja ete saisi a la main.
                  if (entreprise.nom && !getValues('nom')) {
                    setValue('nom', entreprise.nom, { shouldValidate: true });
                  }
                  if (entreprise.adresse && !getValues('adresse')) {
                    setValue('adresse', entreprise.adresse);
                  }
                }}
              />
              {errors.siret && <p className="text-xs text-red-600">{errors.siret.message}</p>}

              <input className="input" placeholder="Nom de l'entreprise / particulier" {...register('nom')} />
              {errors.nom && <p className="text-xs text-red-600">{errors.nom.message}</p>}

              {/* Nom d'une personne, contrairement à la raison sociale
                  au-dessus : il suit la règle du comité. */}
              <input
                className="input"
                placeholder="Nom du contact"
                {...register('contactNom', {
                  onChange: (e) => setValue('contactNom', nomFamille(e.target.value)),
                })}
              />
              {errors.contactNom && <p className="text-xs text-red-600">{errors.contactNom.message}</p>}

              <input className="input" placeholder="Email" type="email" {...register('email')} />
              {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}

              <input className="input" placeholder="Téléphone" {...register('telephone')} />
              <textarea className="input min-h-[80px]" placeholder="Adresse complète" {...register('adresse')} />
              <input className="input" placeholder="Site web (optionnel)" {...register('siteWeb')} />

              {connu && (
                <button
                  type="button"
                  onClick={() => setCoordonneesOuvertes(false)}
                  className="text-sm text-gray-500 dark:text-gray-400"
                >
                  Replier
                </button>
              )}
                </>
              )}
            </section>

            {/* Plans */}
            <section className="card space-y-4 lg:p-6">
              <h2 className="font-semibold lg:text-lg">Choisissez votre plan de visibilité</h2>

              <div className="space-y-2">
                <p className="text-sm font-medium">Livret</p>
                <select className="input" {...register('planLivret')}>
                  <option value="">Aucun</option>
                  {plans?.livret?.map((p: any) => (
                    <option key={p.key} value={p.key}>
                      {p.label} — {p.montant > 0 ? `${p.montant} €` : 'montant libre'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Écocup</p>
                <select className="input" {...register('planEcocup')}>
                  <option value="">Aucun</option>
                  {plans?.ecocup?.map((p: any) => (
                    <option key={p.key} value={p.key}>
                      {p.label} — {p.montant} €
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">T-shirts / textiles</p>
                <select className="input" {...register('planTshirt')}>
                  <option value="">Aucun</option>
                  {plans?.tshirt?.map((p: any) => (
                    <option key={p.key} value={p.key}>
                      {p.label} — {p.montant} €
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Montant estimé</span>
                <span className="text-xl font-bold text-primary-700">{montant} €</span>
              </div>
            </section>
          </div>

          {/* Upload */}
          <section className="card space-y-4 lg:p-6">
            <h2 className="font-semibold lg:text-lg">Vos visuels</h2>

            {aDesVisuels && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Le comité détient déjà ces éléments. Ils repartent en validation avec votre
                  demande — le bureau les revoit avant impression.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <VisuelDetenu
                    titre="Logo haute définition"
                    fichier={visuelsDetenus.logoHd ?? null}
                  />
                  <VisuelDetenu
                    titre="Visuel publicitaire"
                    fichier={visuelsDetenus.visuel ?? null}
                  />
                </div>

                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={reprendreVisuels}
                    onChange={(e) => setReprendreVisuels(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Reprendre ces éléments</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Décochez si vos visuels ont changé : vous en déposerez de nouveaux
                      ci-dessous.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {(!aDesVisuels || !reprendreVisuels) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FileUpload
                  label="Logo haute définition (vectoriel préféré)"
                  selectedFile={logoFile}
                  onFileSelect={setLogoFile}
                  helperText="PNG, JPG, SVG, PDF — max 10 Mo"
                />
                <FileUpload
                  label="Visuel publicitaire"
                  selectedFile={visuelFile}
                  onFileSelect={setVisuelFile}
                  helperText="PNG, JPG, SVG, PDF — max 10 Mo"
                />
              </div>
            )}

            {uploadStatus && (
              <p className={`text-sm ${uploadStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                {uploadStatus.message}
              </p>
            )}
          </section>

          {/* Consentement RGPD */}
          <div className="flex items-start gap-2">
            <input id="consentement" type="checkbox" {...register('consentement')} className="mt-1" />
            <label htmlFor="consentement" className="text-xs lg:text-sm text-gray-600 dark:text-gray-400">
              J'accepte que mes données soient traitées dans le cadre de ma demande de sponsoring
              (conformément à la politique de confidentialité).
            </label>
          </div>
          {errors.consentement && <p className="text-xs text-red-600">{errors.consentement.message}</p>}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary w-full lg:w-auto lg:px-8 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mutation.isPending ? 'Envoi en cours…' : 'Envoyer ma demande'}
          </button>

          {mutation.isError && (
            <p className="text-sm text-red-600">
              Une erreur est survenue. Veuillez réessayer.
            </p>
          )}
        </form>
      )}
    </div>
  );
}
